import React from 'react';

/**
 * Aspect Ratio Icon component
 * Renders a proportionally accurate rectangle outline to visually represent the aspect ratio.
 * The icon container is always `size x size`, and the rectangle inside is scaled to match
 * the actual proportions of the given aspect ratio.
 */
export const AspectRatioIcon = ({ ratio, size = 16, className = '' }) => {
  const iconConfig = ASPECT_RATIO_ICON_PROPS[ratio] || ASPECT_RATIO_ICON_PROPS['1:1'];
  const { widthFraction, heightFraction } = iconConfig;

  // Calculate pixel dimensions within the icon canvas
  const rectWidth = Math.round(size * widthFraction);
  const rectHeight = Math.round(size * heightFraction);
  const x = Math.round((size - rectWidth) / 2);
  const y = Math.round((size - rectHeight) / 2);

  if (ratio === 'Auto') {
    // Auto icon: "A" letterform inside a rounded rect — clean and readable at small sizes
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={`flex-shrink-0 ${className}`} style={{ minWidth: size, minHeight: size }}>
        <rect
          x={1.5}
          y={1.5}
          width={13}
          height={13}
          rx={2.5}
          stroke="currentColor"
          strokeWidth={1.5}
        />
        <path
          d="M8 4.5L5.5 11.5h1.3l.5-1.5h1.4l.5 1.5h1.3L8 4.5zm-.4 4.2l.4-1.3.4 1.3h-.8z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" className={`flex-shrink-0 ${className}`} style={{ minWidth: size, minHeight: size }}>
      <rect
        x={x}
        y={y}
        width={rectWidth}
        height={rectHeight}
        rx={1}
        stroke="currentColor"
        strokeWidth={1.5}
      />
    </svg>
  );
};

// Width/height fractions relative to icon canvas size — these produce
// visually distinct proportioned rectangles matching the reference image.
const ASPECT_RATIO_ICON_PROPS = {
  'Auto':  { widthFraction: 1,    heightFraction: 1    },
  '21:9':  { widthFraction: 0.95, heightFraction: 0.41 },
  '16:9':  { widthFraction: 0.95, heightFraction: 0.53 },
  '5:4':   { widthFraction: 0.90, heightFraction: 0.72 },
  '4:3':   { widthFraction: 0.90, heightFraction: 0.68 },
  '3:2':   { widthFraction: 0.90, heightFraction: 0.60 },
  '1:1':   { widthFraction: 0.80, heightFraction: 0.80 },
  '4:5':   { widthFraction: 0.72, heightFraction: 0.90 },
  '3:4':   { widthFraction: 0.68, heightFraction: 0.90 },
  '2:3':   { widthFraction: 0.60, heightFraction: 0.90 },
  '9:16':  { widthFraction: 0.53, heightFraction: 0.95 },
};

/**
 * Single source of truth for aspect ratios across all tools.
 * Order matches the reference UI: Auto → widest horizontal → square → tallest vertical.
 */
const aspectRatios = [
  { label: 'Auto',             value: 'Auto', width: 1024, height: 1024 },
  { label: 'Ultra Wide (21:9)', value: '21:9', width: 1584, height: 672  },
  { label: 'Widescreen (16:9)', value: '16:9', width: 1376, height: 768  },
  { label: 'Classic (5:4)',     value: '5:4',  width: 1152, height: 928  },
  { label: 'Landscape (4:3)',   value: '4:3',  width: 1200, height: 896  },
  { label: 'Wide (3:2)',        value: '3:2',  width: 1264, height: 848  },
  { label: 'Square (1:1)',      value: '1:1',  width: 1024, height: 1024 },
  { label: 'Standard (4:5)',    value: '4:5',  width: 928,  height: 1152 },
  { label: 'Portrait (3:4)',    value: '3:4',  width: 896,  height: 1200 },
  { label: 'Tall (2:3)',        value: '2:3',  width: 848,  height: 1264 },
  { label: 'Vertical (9:16)',   value: '9:16', width: 768,  height: 1376 },
];

export default aspectRatios;
